"use client";

import { useEffect, useRef, useState } from "react";
import type { Category as SearchTabCategory } from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/types";

const DEFAULT_HITS_PER_PAGE = 8;

/** POST `/api/trending-products`; `data` is set after success, or fallback on error. Ref avoids stale `fallbackCategories` without listing it in deps. */
export default function useTrendingProductsApi({
  categories,
  fallbackCategories,
  enabled,
  hitsPerPage = DEFAULT_HITS_PER_PAGE,
}: {
  categories: SearchTabCategory[];
  fallbackCategories: SearchTabCategory[];
  enabled: boolean;
  hitsPerPage?: number;
}) {
  const [data, setData] = useState<SearchTabCategory[] | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const fallbackRef = useRef(fallbackCategories);
  fallbackRef.current = fallbackCategories;

  const requestKey = JSON.stringify({
    categories: categories,
    hitsPerPage,
  });

  useEffect(() => {
    if (!enabled) {
      setData(undefined);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    void (async () => {
      try {
        const res = await fetch("/api/trending-products", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            categories: categories,
            hitsPerPage,
          }),
        });
        const json = (await res.json()) as SearchTabCategory[];
        if (!cancelled) {
          setData(res.ok ? json : fallbackRef.current);
        }
      } catch {
        if (!cancelled) {
          setData(fallbackRef.current);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, requestKey]);

  return { data, isLoading };
}
